<template>
  <div class="min-h-full p-4">
    <div class="container">
      <button type="button" @click="handleBack" class="mb-6 back-btn">
        <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
        返回
      </button>

      <div class="header">
        <div class="inline-flex items-center justify-center w-16 h-16 gradient-button rounded-full mb-4 shadow-lg">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
        </div>
        <h1 class="title">MCP 对接文档</h1>
        <p class="subtitle">通过 Streamable HTTP 将 hash-to-phone 能力接入 MCP 客户端</p>
      </div>

      <div class="disclaimer" role="note">
        本功能为实验性功能，不做 SLA 保证，请勿在生产环境或业务核心链路上使用。
      </div>

      <div class="main-card">
        <h2 class="section-title">概述</h2>
        <p class="section-desc">
          本服务提供符合 MCP 规范的 Streamable HTTP 端点，供 Cursor、Claude Desktop 等 MCP 客户端调用。
          鉴权方式与 HTTP 公共接口相同，使用 API Key。
          在 <router-link to="/settings/api-keys" class="link">API Key 管理</router-link> 创建 Key 后即可配置。
        </p>
      </div>

      <div class="main-card">
        <h2 class="section-title">服务地址</h2>
        <p class="section-desc mb-3"></p>
        <div class="code-block">{{ baseUrl }}</div>
      </div>

      <div class="main-card">
        <h2 class="section-title">端点</h2>
        <div class="endpoint">
          <span class="method any">POST/GET</span>
          <code class="path">{{ baseUrl }}/mcp</code>
        </div>
        <p class="section-desc mt-3">传输协议：MCP Streamable HTTP（JSON-RPC over HTTP，支持 SSE 流式响应）。</p>
      </div>

      <div class="main-card">
        <h2 class="section-title">鉴权</h2>
        <p class="section-desc mb-3">在 MCP 客户端配置中设置请求头：</p>
        <div class="code-block">X-API-Key: ph_你的密钥</div>
      </div>

      <div class="main-card">
        <h2 class="section-title">可用工具</h2>
        <table class="doc-table">
          <thead>
            <tr><th>工具名</th><th>说明</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><code>hash_to_phone</code></td>
              <td>根据 MD5、SHA256 或国密 SM3 摘要查询手机号</td>
            </tr>
          </tbody>
        </table>

        <h3 class="sub-title">hash_to_phone 参数</h3>
        <div class="code-block">{ "hash": "bfdc153c0a635d3d49c082afeb4e0bba" }</div>

        <h3 class="sub-title">返回 structuredContent 示例</h3>
        <div class="code-block">{
  "hash": "bfdc153c0a635d3d49c082afeb4e0bba",
  "phone": "13000000001",
  "found": true,
  "hash_type": "MD5",
  "duration_ms": 12
}</div>
      </div>

      <div class="main-card">
        <h2 class="section-title">Cursor 配置示例</h2>
        <p class="section-desc mb-3">在项目 <code class="code-inline">.cursor/mcp.json</code> 或 Cursor 设置 → MCP 中添加：</p>
        <div class="code-block">{
  "mcpServers": {
    "phone-hash": {
      "url": "{{ baseUrl }}/mcp",
      "headers": {
        "X-API-Key": "ph_你的密钥"
      }
    }
  }
}</div>
        <p class="hint mt-3">请将 URL 与 Key 替换为实际值。生产环境建议使用 HTTPS。</p>
      </div>

      <div class="main-card">
        <h2 class="section-title">Claude Desktop 配置示例</h2>
        <p class="section-desc mb-3">编辑配置文件（macOS: <code class="code-inline">~/Library/Application Support/Claude/claude_desktop_config.json</code>）：</p>
        <div class="code-block">{
  "mcpServers": {
    "phone-hash": {
      "url": "{{ baseUrl }}/mcp",
      "headers": {
        "X-API-Key": "ph_你的密钥"
      }
    }
  }
}</div>
      </div>

      <div class="main-card">
        <h2 class="section-title">限流与错误</h2>
        <ul class="param-list">
          <li>每个 API Key 每分钟最多 30 次（HTTP API 与 MCP 合计；与页面单次查询频控一致，独立计数）</li>
          <li>Key 无效或缺失：HTTP 401</li>
          <li>超出限流：HTTP 429</li>
          <li>工具内 hash 格式错误：在返回的 <code class="code-inline">phone</code> 字段中给出说明，不视为协议错误</li>
        </ul>
        <p class="section-desc mt-4">HTTP 接口细节见 <router-link to="/docs/api" class="link">HTTP 接口文档</router-link>。</p>
      </div>
    </div>
  </div>
</template>

<script>
import { PUBLIC_API_URL } from '../utils/seo.js';

export default {
  name: 'McpDocs',
  computed: {
    baseUrl() {
      return PUBLIC_API_URL;
    }
  },
  methods: {
    handleBack() {
      this.$router.push('/single');
    }
  }
};
</script>

<style scoped>
.container { max-width: 800px; margin: 0 auto; }
.header { text-align: center; margin-bottom: 2rem; }
.title { font-size: 1.75rem; font-weight: 700; color: #111827; margin-bottom: 0.5rem; }
.subtitle { color: #6b7280; }
.disclaimer {
  margin-bottom: 1.5rem;
  padding: 0.875rem 1rem;
  border-radius: 0.75rem;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  color: #92400e;
  font-size: 0.875rem;
  line-height: 1.6;
  text-align: center;
}
.back-btn {
  display: flex; align-items: center; gap: 0.5rem;
  background: linear-gradient(to right, #6366f1, #4f46e5);
  color: white; padding: 0.375rem 0.75rem; border-radius: 0.75rem;
  font-weight: 500; transition: all 0.3s;
}
.back-btn:hover { filter: brightness(1.05); }
.main-card {
  background: rgba(255,255,255,0.85); backdrop-filter: blur(12px);
  border-radius: 1.5rem; padding: 1.5rem; margin-bottom: 1.5rem;
  box-shadow: 0 10px 40px rgba(0,0,0,0.06); border: 1px solid rgba(255,255,255,0.3);
}
.section-title { font-size: 1.125rem; font-weight: 600; color: #111827; margin-bottom: 0.5rem; }
.sub-title { font-size: 0.9375rem; font-weight: 600; color: #374151; margin: 1.25rem 0 0.5rem; }
.section-desc { font-size: 0.875rem; color: #6b7280; line-height: 1.6; }
.hint { font-size: 0.8125rem; color: #9ca3af; }
.link { color: #2563eb; text-decoration: underline; }
.code-block {
  background: #1e293b; color: #e2e8f0; padding: 1rem; border-radius: 0.75rem;
  font-family: ui-monospace, monospace; font-size: 0.8125rem; line-height: 1.6;
  overflow-x: auto; white-space: pre-wrap; word-break: break-all;
}
.code-inline {
  background: #f3f4f6; padding: 0.125rem 0.375rem; border-radius: 0.25rem;
  font-family: ui-monospace, monospace; font-size: 0.8125rem;
}
.endpoint { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
.method { font-size: 0.75rem; font-weight: 700; padding: 0.25rem 0.5rem; border-radius: 0.375rem; }
.method.any { background: #f3e8ff; color: #7c3aed; }
.path { font-family: ui-monospace, monospace; font-size: 0.9375rem; color: #111827; word-break: break-all; }
.param-list { margin: 0.75rem 0 0; padding-left: 1.25rem; font-size: 0.875rem; color: #4b5563; line-height: 1.7; }
.doc-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; margin-top: 0.5rem; }
.doc-table th, .doc-table td { border: 1px solid #e5e7eb; padding: 0.625rem 0.75rem; text-align: left; }
.doc-table th { background: #f9fafb; font-weight: 600; color: #374151; }
.doc-table code { font-size: 0.8125rem; }
</style>
